// frontend/src/services/camdobrainService.js
import axios from "axios";

// Configurar URL base según el entorno
const BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api/content', '')
  : 'http://localhost:8000';

// Crear instancia de axios para CamDoBrain
const camdobrainApi = axios.create({
  baseURL: `${BASE_URL}/camdobrain`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir token JWT
camdobrainApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Obtener CSRF token
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Añadir CSRF token a peticiones POST/PUT/DELETE
camdobrainApi.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCookie('csrftoken');
  }
  return config;
});

const camdobrainService = {
  // ==================== BRAND PROFILES ====================

  /**
   * Obtener todos los perfiles de marca
   */
  getBrandProfiles() {
    return camdobrainApi.get("/api/brand-profiles/");
  },

  /**
   * Obtener un perfil de marca por ID
   */
  getBrandProfile(id) {
    return camdobrainApi.get(`/api/brand-profiles/${id}/`);
  },

  /**
   * Crear un nuevo perfil de marca
   */
  createBrandProfile(data) {
    return camdobrainApi.post("/api/brand-profiles/", data);
  },

  /**
   * Actualizar un perfil de marca
   */
  updateBrandProfile(id, data) {
    return camdobrainApi.patch(`/api/brand-profiles/${id}/`, data);
  },

  /**
   * Eliminar un perfil de marca
   */
  deleteBrandProfile(id) {
    return camdobrainApi.delete(`/api/brand-profiles/${id}/`);
  },

  /**
   * Obtener estadísticas de un perfil de marca
   */
  getBrandProfileStats(id) {
    return camdobrainApi.get(`/api/brand-profiles/${id}/stats/`);
  },

  // ==================== TRACKED ACCOUNTS (Competidores y Referencias) ====================
  // NOTA: Las cuentas de competidores y referencias ahora vienen del Instagram Tracker.
  // La gestión de cuentas se hace desde /onebase/instagram/tracker
  // Aquí solo se pueden consultar (solo lectura).

  /**
   * Obtener todas las cuentas rastreadas disponibles para usar como competidores/referencias
   * @param {string} search - Filtrar por username (opcional)
   */
  getTrackedAccounts(search = null) {
    const params = {};
    if (search) params.search = search;
    return camdobrainApi.get("/api/tracked-accounts/", { params });
  },

  /**
   * Obtener una cuenta rastreada por ID
   */
  getTrackedAccount(id) {
    return camdobrainApi.get(`/api/tracked-accounts/${id}/`);
  },

  /**
   * Obtener posts de una cuenta rastreada
   */
  getTrackedAccountPosts(id) {
    return camdobrainApi.get(`/api/tracked-accounts/${id}/posts/`);
  },

  /**
   * Obtener análisis de una cuenta rastreada para CamDoBrain
   */
  getTrackedAccountAnalysis(id) {
    return camdobrainApi.get(`/api/tracked-accounts/${id}/analysis/`);
  },

  // ==================== CONTENT GENERATION ====================

  /**
   * Generar recomendación de contenido
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} targetDate - Fecha objetivo (opcional, formato YYYY-MM-DD)
   * @param {object} context - Contexto adicional (tendencias, noticias, etc.)
   */
  generateRecommendation(brandProfileId, targetDate = null, context = null) {
    const data = { brand_profile_id: brandProfileId };
    if (targetDate) {
      data.target_date = targetDate;
    }
    // Añadir contexto de los pasos del wizard
    if (context) {
      if (context.trends && context.trends.length > 0) {
        data.selected_trends = context.trends;
      }
      if (context.competitor_analysis) {
        data.competitor_insights = context.competitor_analysis;
      }
      data.context = {
        news_count: context.news_count || 0,
        competitor_count: context.competitor_count || 0,
        training_samples: context.training_samples || 0
      };
    }
    return camdobrainApi.post("/api/generate-recommendation/", data);
  },

  /**
   * Generar plan semanal de contenido
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} numPosts - Número de posts a planificar (1-10)
   * @param {object} context - Contexto adicional (tendencias, noticias, etc.)
   */
  generateWeeklyPlan(brandProfileId, numPosts = 5, context = null) {
    const data = {
      brand_profile_id: brandProfileId,
      num_posts: numPosts,
    };
    // Añadir contexto de los pasos del wizard
    if (context) {
      if (context.trends && context.trends.length > 0) {
        data.selected_trends = context.trends;
      }
      if (context.competitor_analysis) {
        data.competitor_insights = context.competitor_analysis;
      }
      data.context = {
        news_count: context.news_count || 0,
        competitor_count: context.competitor_count || 0,
        training_samples: context.training_samples || 0
      };
    }
    return camdobrainApi.post("/api/generate-weekly-plan/", data);
  },

  /**
   * Analizar una idea propuesta por el usuario
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} idea - Descripción de la idea
   */
  analyzeIdea(brandProfileId, idea) {
    return camdobrainApi.post("/api/analyze-idea/", {
      brand_profile_id: brandProfileId,
      idea: idea,
    });
  },

  // ==================== PREDICTIONS ====================

  /**
   * Obtener todas las predicciones
   * @param {number} brandProfileId - Filtrar por perfil de marca (opcional)
   * @param {string} status - Filtrar por estado (opcional)
   */
  getPredictions(brandProfileId = null, status = null) {
    const params = {};
    if (brandProfileId) params.brand_profile_id = brandProfileId;
    if (status) params.status = status;
    return camdobrainApi.get("/api/predictions/", { params });
  },

  /**
   * Obtener una predicción por ID
   */
  getPrediction(id) {
    return camdobrainApi.get(`/api/predictions/${id}/`);
  },

  /**
   * Aprobar una predicción
   * @param {number} id - ID de la predicción
   * @param {object} modifications - Modificaciones del usuario (opcional)
   * @param {string} scheduledFor - Fecha/hora programada (opcional)
   */
  approvePrediction(id, modifications = null, scheduledFor = null) {
    const data = { action: 'approve' };
    if (modifications) data.modifications = modifications;
    if (scheduledFor) data.scheduled_for = scheduledFor;
    return camdobrainApi.post(`/api/predictions/${id}/approve/`, data);
  },

  /**
   * Rechazar una predicción
   * @param {number} id - ID de la predicción
   * @param {string} feedback - Feedback del usuario
   */
  rejectPrediction(id, feedback = '') {
    return camdobrainApi.post(`/api/predictions/${id}/approve/`, {
      action: 'reject',
      feedback: feedback,
    });
  },

  /**
   * Registrar performance real de una predicción
   * @param {number} id - ID de la predicción
   * @param {object} actualMetrics - Métricas reales
   */
  recordPerformance(id, actualMetrics) {
    return camdobrainApi.post(`/api/predictions/${id}/record_performance/`, {
      actual_metrics: actualMetrics,
    });
  },

  // ==================== MODEL INSIGHTS ====================

  /**
   * Obtener insights del modelo
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getModelInsights(brandProfileId) {
    return camdobrainApi.get("/api/model-insights/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Entrenar modelo ML manualmente
   * @param {number} brandProfileId - ID del perfil de marca
   */
  trainModel(brandProfileId) {
    return camdobrainApi.post("/api/train-model/", {
      brand_profile_id: brandProfileId,
    });
  },

  /**
   * Registrar performance real de una prediccion (feedback loop)
   * @param {number} predictionId - ID de la prediccion
   */
  recordActualPerformance(predictionId) {
    return camdobrainApi.post("/api/record-performance/", {
      prediction_id: predictionId,
    });
  },

  // ==================== TREND SNAPSHOTS ====================

  /**
   * Obtener snapshots de tendencias
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getTrendSnapshots(brandProfileId) {
    return camdobrainApi.get("/api/trend-snapshots/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener último snapshot de tendencias
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getLatestTrends(brandProfileId) {
    return camdobrainApi.get("/api/trend-snapshots/latest/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  // ==================== CALENDAR EVENTS ====================

  /**
   * Obtener eventos del calendario
   * @param {number} brandProfileId - ID del perfil de marca (opcional)
   * @param {string} startDate - Fecha inicial (opcional)
   * @param {string} endDate - Fecha final (opcional)
   */
  getCalendarEvents(brandProfileId = null, startDate = null, endDate = null) {
    const params = {};
    if (brandProfileId) params.brand_profile_id = brandProfileId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return camdobrainApi.get("/api/calendar-events/", { params });
  },

  /**
   * Crear evento de calendario
   */
  createCalendarEvent(data) {
    return camdobrainApi.post("/api/calendar-events/", data);
  },

  /**
   * Actualizar evento de calendario
   */
  updateCalendarEvent(id, data) {
    return camdobrainApi.patch(`/api/calendar-events/${id}/`, data);
  },

  /**
   * Eliminar evento de calendario
   */
  deleteCalendarEvent(id) {
    return camdobrainApi.delete(`/api/calendar-events/${id}/`);
  },

  // ==================== DIAGNOSTICS ====================

  /**
   * Ejecutar diagnóstico de cuenta
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {boolean} useClaude - Usar Claude API (default: true)
   */
  runDiagnostic(brandProfileId, useClaude = true) {
    return camdobrainApi.post("/api/run-diagnostic/", {
      brand_profile_id: brandProfileId,
      use_claude: useClaude,
    });
  },

  /**
   * Obtener último diagnóstico de un perfil
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getLatestDiagnostic(brandProfileId) {
    return camdobrainApi.get("/api/diagnostics/latest/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener todos los diagnósticos de un perfil
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getDiagnostics(brandProfileId) {
    return camdobrainApi.get("/api/diagnostics/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener un diagnóstico por ID
   * @param {number} id - ID del diagnóstico
   */
  getDiagnostic(id) {
    return camdobrainApi.get(`/api/diagnostics/${id}/`);
  },

  // ==================== STRATEGIC OBJECTIVES ====================

  /**
   * Generar objetivos estratégicos
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} diagnosticId - ID del diagnóstico (opcional)
   * @param {boolean} useClaude - Usar Claude API (default: true)
   */
  generateObjectives(brandProfileId, diagnosticId = null, useClaude = true) {
    return camdobrainApi.post("/api/generate-objectives/", {
      brand_profile_id: brandProfileId,
      diagnostic_id: diagnosticId,
      use_claude: useClaude,
    });
  },

  /**
   * Obtener objetivos estratégicos
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} status - Filtrar por estado (opcional)
   */
  getObjectives(brandProfileId = null, status = null) {
    const params = {};
    if (brandProfileId) params.brand_profile_id = brandProfileId;
    if (status) params.status = status;
    return camdobrainApi.get("/api/objectives/", { params });
  },

  /**
   * Obtener un objetivo por ID
   * @param {number} id - ID del objetivo
   */
  getObjective(id) {
    return camdobrainApi.get(`/api/objectives/${id}/`);
  },

  /**
   * Activar un objetivo
   * @param {number} id - ID del objetivo
   */
  activateObjective(id) {
    return camdobrainApi.post(`/api/objectives/${id}/activate/`);
  },

  /**
   * Completar un objetivo
   * @param {number} id - ID del objetivo
   * @param {number} rating - Valoración (1-5)
   * @param {string} notes - Notas opcionales
   */
  completeObjective(id, rating = null, notes = '') {
    const data = {};
    if (rating) data.rating = rating;
    if (notes) data.notes = notes;
    return camdobrainApi.post(`/api/objectives/${id}/complete/`, data);
  },

  /**
   * Pausar un objetivo
   * @param {number} id - ID del objetivo
   */
  pauseObjective(id) {
    return camdobrainApi.post(`/api/objectives/${id}/pause/`);
  },

  /**
   * Actualizar progreso de un objetivo
   * @param {number} id - ID del objetivo
   */
  trackObjectiveProgress(id) {
    return camdobrainApi.post(`/api/objectives/${id}/track_progress/`);
  },

  /**
   * Actualizar estado de completado de una táctica
   * @param {number} id - ID del objetivo
   * @param {number} tacticIndex - Índice de la táctica
   * @param {boolean} completed - Estado de completado
   */
  updateTactic(id, tacticIndex, completed) {
    return camdobrainApi.post(`/api/objectives/${id}/update_tactic/`, {
      tactic_index: tacticIndex,
      completed: completed,
    });
  },

  // ==================== NEWS (Google News RSS) ====================

  /**
   * Obtener noticias relevantes para una marca
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} maxPerSource - Máximo de noticias por fuente (default: 10)
   */
  fetchNews(brandProfileId, maxPerSource = 10) {
    return camdobrainApi.post("/api/fetch-news/", {
      brand_profile_id: brandProfileId,
      max_per_source: maxPerSource,
    });
  },

  /**
   * Obtener noticias guardadas de un perfil
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} topic - Filtrar por tópico (opcional)
   * @param {number} days - Días hacia atrás (default: 7)
   */
  getNews(brandProfileId = null, topic = null, days = 7) {
    const params = {};
    if (brandProfileId) params.brand_profile_id = brandProfileId;
    if (topic) params.topic = topic;
    if (days) params.days = days;
    return camdobrainApi.get("/api/news/", { params });
  },

  /**
   * Obtener noticias recientes
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} days - Días hacia atrás para buscar (default: 7)
   * @param {number} limit - Límite de resultados (default: 50)
   */
  getRecentNews(brandProfileId = null, days = 7, limit = 50) {
    const params = { days, limit };
    if (brandProfileId) params.brand_profile_id = brandProfileId;
    return camdobrainApi.get("/api/news/recent/", { params });
  },

  /**
   * Aceptar una noticia
   * @param {number} newsId - ID de la noticia
   */
  acceptNews(newsId) {
    return camdobrainApi.post(`/api/news/${newsId}/accept/`);
  },

  /**
   * Rechazar una noticia y obtener reemplazo
   * @param {number} newsId - ID de la noticia
   * @param {Array} currentIds - IDs de noticias actualmente mostradas
   */
  rejectNews(newsId, currentIds = []) {
    return camdobrainApi.post(`/api/news/${newsId}/reject/`, { current_ids: currentIds });
  },

  /**
   * Obtener noticias agrupadas por tópico
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getNewsByTopic(brandProfileId) {
    return camdobrainApi.get("/api/news/by_topic/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Buscar noticias por keyword (directo de Google News)
   * @param {string} query - Término de búsqueda
   * @param {string} country - Código de país (default: CL)
   * @param {number} maxResults - Máximo de resultados (default: 20)
   */
  searchNews(query, country = 'CL', maxResults = 20) {
    return camdobrainApi.get("/api/search-news/", {
      params: { query, country, max_results: maxResults },
    });
  },

  /**
   * Obtener noticias por tópico (directo de Google News)
   * @param {string} topic - Tópico (TECHNOLOGY, BUSINESS, etc.)
   * @param {string} country - Código de país (default: CL)
   * @param {number} maxResults - Máximo de resultados (default: 20)
   */
  getTopicNews(topic, country = 'CL', maxResults = 20) {
    return camdobrainApi.get("/api/topic-news/", {
      params: { topic, country, max_results: maxResults },
    });
  },

  /**
   * Obtener titulares principales (directo de Google News)
   * @param {string} country - Código de país (default: CL)
   * @param {number} maxResults - Máximo de resultados (default: 20)
   */
  getHeadlines(country = 'CL', maxResults = 20) {
    return camdobrainApi.get("/api/headlines/", {
      params: { country, max_results: maxResults },
    });
  },

  /**
   * Analizar noticias y generar oportunidades de contenido
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} limit - Cantidad de noticias a analizar (default: 10)
   */
  analyzeNews(brandProfileId, limit = 10) {
    return camdobrainApi.post("/api/analyze-news/", {
      brand_profile_id: brandProfileId,
      limit: limit,
    });
  },

  /**
   * Obtener tópicos y países disponibles
   */
  getNewsTopics() {
    return camdobrainApi.get("/api/news-topics/");
  },

  /**
   * Obtener último snapshot de noticias
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getLatestNewsSnapshot(brandProfileId) {
    return camdobrainApi.get("/api/news-snapshots/latest/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener configuración de búsqueda de noticias
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getNewsConfig(brandProfileId) {
    return camdobrainApi.get(`/api/brand-profiles/${brandProfileId}/news_config/`);
  },

  /**
   * Actualizar configuración de búsqueda de noticias
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {object} config - Configuración de búsqueda
   * @param {string[]} config.custom_keywords - Keywords personalizados
   * @param {boolean} config.include_competitors - Incluir keywords de competidores
   * @param {boolean} config.include_industry - Incluir keywords de la industria
   * @param {boolean} config.include_trends - Incluir keywords de tendencias
   * @param {string[]} config.countries - Países para búsqueda
   */
  updateNewsConfig(brandProfileId, config) {
    return camdobrainApi.post(`/api/brand-profiles/${brandProfileId}/news_config/`, config);
  },

  /**
   * Obtener todos los snapshots de noticias
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getNewsSnapshots(brandProfileId) {
    return camdobrainApi.get("/api/news-snapshots/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  // ==================== CONTENT SUGGESTIONS ====================

  /**
   * Obtener sugerencias de contenido
   * @param {object} params - Parámetros de filtrado
   * @param {number} params.brand_profile_id - ID del perfil de marca
   * @param {string} params.status - Filtrar por estado (opcional)
   * @param {string} params.suggestion_type - Filtrar por tipo (opcional)
   */
  getSuggestions(params = {}) {
    return camdobrainApi.get("/api/suggestions/", { params });
  },

  /**
   * Obtener una sugerencia por ID
   */
  getSuggestion(id) {
    return camdobrainApi.get(`/api/suggestions/${id}/`);
  },

  /**
   * Generar nuevas sugerencias de contenido
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} suggestionType - Tipo de sugerencia (news_based, objective_based, etc.)
   */
  generateSuggestions(brandProfileId, suggestionType = null) {
    const data = { brand_profile_id: brandProfileId };
    if (suggestionType) data.suggestion_type = suggestionType;
    return camdobrainApi.post("/api/suggestions/generate/", data);
  },

  /**
   * Convertir sugerencia en ContentPost
   * @param {number} id - ID de la sugerencia
   * @param {number} influencerId - ID del influencer (opcional)
   * @param {number} platformId - ID de la plataforma (opcional)
   */
  convertSuggestionToPost(id, influencerId = null, platformId = null) {
    const data = {};
    if (influencerId) data.influencer_id = influencerId;
    if (platformId) data.platform_id = platformId;
    return camdobrainApi.post(`/api/suggestions/${id}/convert_to_post/`, data);
  },

  /**
   * Rechazar una sugerencia
   * @param {number} id - ID de la sugerencia
   * @param {string} reason - Razón del rechazo
   */
  rejectSuggestion(id, reason = '') {
    return camdobrainApi.post(`/api/suggestions/${id}/reject/`, { reason });
  },

  // ==================== SMART CALENDAR ====================

  /**
   * Obtener datos del calendario inteligente
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} startDate - Fecha inicial (YYYY-MM-DD)
   * @param {string} endDate - Fecha final (YYYY-MM-DD)
   */
  getCalendarData(brandProfileId, startDate, endDate) {
    return camdobrainApi.get("/api/calendar/calendar_data/", {
      params: { brand_profile_id: brandProfileId, start_date: startDate, end_date: endDate },
    });
  },

  /**
   * Sincronizar calendario con todas las fuentes
   * @param {number} brandProfileId - ID del perfil de marca
   */
  syncCalendar(brandProfileId) {
    return camdobrainApi.post("/api/calendar/sync/", {
      brand_profile_id: brandProfileId,
    });
  },

  /**
   * Obtener horarios óptimos de publicación
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getOptimalPostingTimes(brandProfileId) {
    return camdobrainApi.get("/api/calendar/optimal_times/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener entradas del calendario
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {string} startDate - Fecha inicial
   * @param {string} endDate - Fecha final
   * @param {string} type - Tipo de entrada (opcional)
   */
  getCalendarEntries(brandProfileId, startDate = null, endDate = null, type = null) {
    const params = { brand_profile_id: brandProfileId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (type) params.type = type;
    return camdobrainApi.get("/api/calendar/", { params });
  },

  // ==================== COMPETITOR COMPARISON ====================

  /**
   * Obtener dashboard de competencia
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getCompetitorDashboard(brandProfileId) {
    return camdobrainApi.get("/api/competitor-comparison/dashboard/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Generar nueva comparación con competidores
   * @param {number} brandProfileId - ID del perfil de marca
   */
  generateCompetitorComparison(brandProfileId) {
    return camdobrainApi.post("/api/competitor-comparison/generate/", {
      brand_profile_id: brandProfileId,
    });
  },

  /**
   * Obtener oportunidades de contenido basadas en competencia
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getContentGaps(brandProfileId) {
    return camdobrainApi.get("/api/competitor-comparison/content_gaps/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener historial de comparaciones
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getCompetitorComparisons(brandProfileId) {
    return camdobrainApi.get("/api/competitor-comparison/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Obtener análisis detallado de competencia con IA (Claude)
   * Analiza las últimas 5 publicaciones de cada cuenta y genera diagnóstico
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getDetailedCompetitorAnalysis(brandProfileId) {
    return camdobrainApi.get("/api/competitor-comparison/detailed_analysis/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  /**
   * Analiza visualmente un post individual
   * @param {object} postData - Datos del post {media_url, media_type, caption, thumbnail_url}
   * @returns {Promise} Análisis visual del post
   */
  analyzePostVisual(postData) {
    return camdobrainApi.post("/api/competitor-comparison/analyze_post_visual/", postData);
  },

  /**
   * Obtener resumen de posts de competidores (último registro por post)
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getCompetitorTrackerSummary(brandProfileId, limit = 30, offset = 0, periodos = '') {
    const params = { brand_profile_id: brandProfileId, limit, offset };
    if (periodos) params.periodos = periodos;
    return camdobrainApi.get("/api/competitor-comparison/tracker_summary/", { params });
  },

  // ==================== AUTOMATIONS ====================

  /**
   * Ejecutar todas las automatizaciones manualmente
   * @param {number} brandProfileId - ID del perfil de marca
   */
  runAutomations(brandProfileId) {
    return camdobrainApi.post("/api/run-automations/", {
      brand_profile_id: brandProfileId,
    });
  },

  // ==================== GOOGLE TRENDS COMPLETO ====================

  /**
   * Obtener datos de la página principal de Google Trends
   * @param {string} country - Código de país (default: CL)
   */
  getGoogleTrendsHome(country = 'CL', refresh = false) {
    return camdobrainApi.get("/api/google-trends/home/", {
      params: { country, refresh },
    });
  },

  /**
   * Obtener búsquedas trending del momento
   * @param {string} country - Código de país (default: CL)
   * @param {number} limit - Máximo de resultados (default: 20)
   * @param {boolean} refresh - Forzar recarga sin cache (default: false)
   */
  getGoogleTrendsTrending(country = 'CL', limit = 20, refresh = false) {
    return camdobrainApi.get("/api/google-trends/trending/", {
      params: { country, limit, refresh },
    });
  },

  /**
   * Obtener tendencias del día con contexto
   * @param {string} country - Código de país (default: CL)
   * @param {number} limit - Máximo de resultados (default: 10)
   */
  getGoogleTrendsDaily(country = 'CL', limit = 10) {
    return camdobrainApi.get("/api/google-trends/daily/", {
      params: { country, limit },
    });
  },

  /**
   * Explorar un término específico (función Explorar de Google Trends)
   * @param {string} keyword - Término a explorar
   * @param {string} country - Código de país (default: CL)
   * @param {string} timeframe - Período de tiempo (default: today_3m)
   * @param {string} compare - Keywords adicionales para comparar (separados por coma)
   */
  exploreGoogleTrends(keyword, country = 'CL', timeframe = 'today_3m', compare = null) {
    const params = { keyword, country, timeframe };
    if (compare) params.compare = compare;
    return camdobrainApi.get("/api/google-trends/explore/", { params });
  },

  /**
   * Obtener tendencias en tiempo real
   * @param {string} country - Código de país (default: CL)
   * @param {string} category - Categoría (default: all)
   */
  getGoogleTrendsRealtime(country = 'CL', category = 'all') {
    return camdobrainApi.get("/api/google-trends/realtime/", {
      params: { country, category },
    });
  },

  /**
   * Obtener sugerencias de búsqueda para autocompletar
   * @param {string} keyword - Término para obtener sugerencias
   */
  getGoogleTrendsSuggestions(keyword) {
    return camdobrainApi.get("/api/google-trends/suggestions/", {
      params: { keyword },
    });
  },

  /**
   * Comparar múltiples términos en Google Trends
   * @param {string[]} keywords - Array de términos a comparar
   * @param {string} country - Código de país (default: CL)
   * @param {string} timeframe - Período de tiempo (default: today_12m)
   */
  compareGoogleTrends(keywords, country = 'CL', timeframe = 'today_12m') {
    return camdobrainApi.get("/api/google-trends/compare/", {
      params: { keywords: keywords.join(','), country, timeframe },
    });
  },

  /**
   * Obtener metadata de Google Trends (países, categorías, timeframes)
   */
  getGoogleTrendsMetadata() {
    return camdobrainApi.get("/api/google-trends/metadata/");
  },

  /**
   * Obtener tendencias relevantes para un perfil de marca
   * @param {number} brandProfileId - ID del perfil de marca
   */
  getGoogleTrendsForBrand(brandProfileId) {
    return camdobrainApi.get("/api/google-trends/for-brand/", {
      params: { brand_profile_id: brandProfileId },
    });
  },

  // =========================================================================
  // FECHAS IMPORTANTES
  // =========================================================================

  /**
   * Obtener fechas importantes próximas
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {number} days - Días hacia adelante (default: 30)
   */
  getUpcomingDates(brandProfileId, days = 30) {
    return camdobrainApi.get("/api/important-dates/upcoming/", {
      params: { brand_profile_id: brandProfileId, days },
    });
  },

  /**
   * Obtener todas las fechas importantes
   * @param {number} brandProfileId - ID del perfil de marca
   * @param {boolean} selectedOnly - Solo fechas seleccionadas
   */
  getImportantDates(brandProfileId, selectedOnly = false) {
    const params = { brand_profile_id: brandProfileId };
    if (selectedOnly) params.selected_only = 'true';
    return camdobrainApi.get("/api/important-dates/", { params });
  },

  /**
   * Alternar selección de una fecha
   * @param {number} dateId - ID de la fecha
   */
  toggleDateSelection(dateId) {
    return camdobrainApi.post(`/api/important-dates/${dateId}/toggle_select/`);
  },

  /**
   * Crear fechas predefinidas para un país
   * @param {number} brandProfileId - ID del perfil de marca (opcional)
   * @param {string} country - Código de país (default: CL)
   */
  seedImportantDates(brandProfileId = null, country = 'CL') {
    const data = { country };
    if (brandProfileId) data.brand_profile_id = brandProfileId;
    return camdobrainApi.post("/api/important-dates/seed_dates/", data);
  },

  /**
   * Crear fecha importante personalizada
   */
  createImportantDate(data) {
    return camdobrainApi.post("/api/important-dates/", data);
  },

  // ==================== Q-LEARNING ====================

  /**
   * Trigger Q-Learning model training
   */
  trainQLearning(brandProfileId) {
    return camdobrainApi.post(`/api/qlearning/${brandProfileId}/train/`);
  },

  /**
   * Get next publication proposal with timing heatmap (unified endpoint)
   */
  getQLearningProposal(brandProfileId) {
    return camdobrainApi.get(`/api/qlearning/${brandProfileId}/propose/`);
  },
};

export default camdobrainService;
